import { System } from '../core/System';
import type { Entity } from '../core/Entity';
import { Transform } from '../components/Transform';
import { Collider } from '../components/Collider';

export interface Collision {
  entityA: Entity;
  entityB: Entity;
}

/**
 * Detects AABB collisions between entities.
 */
export class CollisionSystem extends System {
  requiredComponents = [Transform, Collider];

  private _collisions: Collision[] = [];
  private _collisionMap = new Map<number, Entity[]>();

  update(entities: Entity[], _delta: number): void {
    this._collisions = [];
    this._collisionMap.clear();

    // Check all pairs for collisions
    for (let i = 0; i < entities.length; i++) {
      const entityA = entities[i]!;
      const transformA = entityA.get(Transform);
      const colliderA = entityA.get(Collider);

      for (let j = i + 1; j < entities.length; j++) {
        const entityB = entities[j]!;
        const transformB = entityB.get(Transform);
        const colliderB = entityB.get(Collider);

        // Check layer masks
        if ((colliderA.layer & colliderB.mask) === 0 && (colliderB.layer & colliderA.mask) === 0) {
          continue;
        }

        if (this._checkAABB(transformA, colliderA, transformB, colliderB)) {
          this._collisions.push({ entityA, entityB });
          this._addToCollisionMap(entityA, entityB);
          this._addToCollisionMap(entityB, entityA);
        }
      }
    }
  }

  /**
   * Get all collisions from the last update.
   */
  get collisions(): readonly Collision[] {
    return this._collisions;
  }

  /**
   * Get all entities that are colliding with the given entity.
   */
  getCollisions(entity: Entity): readonly Entity[] {
    return this._collisionMap.get(entity.id) ?? [];
  }

  /**
   * Check if two specific entities are colliding.
   */
  isColliding(entityA: Entity, entityB: Entity): boolean {
    const collisions = this._collisionMap.get(entityA.id);
    return collisions?.includes(entityB) ?? false;
  }

  private _checkAABB(
    transformA: Transform,
    colliderA: Collider,
    transformB: Transform,
    colliderB: Collider
  ): boolean {
    const ax = transformA.x + colliderA.offsetX - colliderA.width / 2;
    const ay = transformA.y + colliderA.offsetY - colliderA.height / 2;
    const bx = transformB.x + colliderB.offsetX - colliderB.width / 2;
    const by = transformB.y + colliderB.offsetY - colliderB.height / 2;

    return (
      ax < bx + colliderB.width &&
      ax + colliderA.width > bx &&
      ay < by + colliderB.height &&
      ay + colliderA.height > by
    );
  }

  private _addToCollisionMap(entity: Entity, other: Entity): void {
    const existing = this._collisionMap.get(entity.id);
    if (existing) {
      existing.push(other);
    } else {
      this._collisionMap.set(entity.id, [other]);
    }
  }
}
